import { defineCrestModule } from '@crest/platform-contracts/modules';
import { platformOptionalActionProviderHostCapability, platformApprovedActionInvocationReceiptCapability, platformOptionalFactProviderHostCapability, platformMyDayHostCapability, platformPortalHostCapability, platformReceiptBoundActionProviderHostCapability, } from '@crest/platform-contracts/hosts';
import { platformMediaAcquisitionCapability } from '@crest/platform-contracts/media-acquisition';
import { platformStorageImportHandoffCapability } from '@crest/platform-contracts/storage';
import { projectsProjectManageAllPermission, projectsProjectRecordPolicyCapability, } from './contracts/projects-project-record-policy.contract.js';
import { projectsProjectReferenceAccessHoldCapability, projectsProjectReferenceCapability, } from './contracts/projects-project-reference.contract.js';
import { projectsProjectOrderReferenceCapability } from './contracts/projects-project-order-reference.contract.js';
import { projectsProjectAccessDecisionCapability } from './contracts/projects-project-access-decision.contract.js';
import { projectsTimeReferenceCapability } from './contracts/projects-time-reference.contract.js';
import { projectsTimeLocationReferenceCapability } from './contracts/projects-time-location-reference.contract.js';
import { projectsInsuranceClaimsReadinessProviderId } from './contracts/projects-insurance-claims-readiness.contract.js';
import { projectsLegacyResolutionProviderId } from './contracts/projects-legacy-resolution.contract.js';
import { projectsFinanceProfitabilityIndexCapability, projectsFinanceProfitabilityIndexProviderId, } from './contracts/projects-finance-profitability-index.contract.js';
import { projectsReportingProjectIndexCapability, projectsReportingProjectIndexProviderId, } from './contracts/projects-reporting-project-index.contract.js';
import { projectsPortalProjectSummaryCapability, projectsPortalProjectSummaryGrant, projectsPortalProjectSummaryProviderId, } from './contracts/projects-portal-project-summary.contract.js';
import { projectsCustomerPortalIntakeAcceptanceCapability, projectsCustomerPortalIntakeAcceptanceProviderId, } from './contracts/projects-customer-portal-intake.contract.js';
import { projectsProjectDetailLauncherCapability } from './contracts/projects-project-detail-launcher.contract.js';
import { projectsAssistantContextCapability, projectsAssistantContextProviderId, } from './contracts/projects-assistant-context.contract.js';
import { projectsAssistantActionProviderCapability, } from './contracts/projects-assistant-actions.contract.js';
import { projectsAssistantNoteActionProviderCapability } from './contracts/projects-assistant-note-actions.contract.js';
import { projectsAssistantTeamActionProviderCapability } from './contracts/projects-assistant-team-actions.contract.js';
import { projectsFullProjectImportCapability } from './contracts/projects-full-project-import.contract.js';
import { projectsImportResolutionCapability } from './contracts/projects-import-resolution.contract.js';
import { projectsProjectStaffImportCapability } from './contracts/projects-project-staff-import.contract.js';
import { projectsProjectFilesImportCapability } from './contracts/projects-project-files-import.contract.js';
import { projectsImportReconciliationCapability } from './import/projects-operational-field-import.contract.js';
export const projectsModuleManifest = defineCrestModule({
    id: 'projects',
    version: '1.0.0',
    kind: 'business',
    required: true,
    requires: ['platform'],
    requiredCapabilities: [
        'platform.tenant-context.v1',
        'platform.module-access.v1',
        'platform.membership-directory.v1',
        'platform.storage.v1',
        platformStorageImportHandoffCapability,
        'platform.migration-run-ledger.v1',
        'platform.import-resolution.v1',
        platformMediaAcquisitionCapability,
        platformOptionalActionProviderHostCapability,
        platformReceiptBoundActionProviderHostCapability,
        platformApprovedActionInvocationReceiptCapability,
        platformOptionalFactProviderHostCapability,
        platformMyDayHostCapability,
        platformPortalHostCapability,
    ],
    optionalCapabilities: [
        'relationships.contacts.v1',
        'relationships.import-resolution.v1',
    ],
    optionalCapabilityFallbacks: [{
            capability: 'relationships.contacts.v1',
            degradedMode: 'projects.degraded.billing-recipient-snapshot.v1',
        }, {
            capability: 'relationships.import-resolution.v1',
            degradedMode: 'projects.degraded.import-without-relationship-references.v1',
        }],
    provides: [
        'projects.projects.v1',
        'projects.project-team-notification-audience.v1',
        projectsProjectRecordPolicyCapability,
        'projects.project-team.v1',
        'projects.closeout-validator.v1',
        'projects.detail-host.v1',
        projectsProjectDetailLauncherCapability,
        'projects.project-files.v1',
        'projects.billing-recipient-snapshot.v1',
        projectsProjectReferenceCapability,
        projectsProjectReferenceAccessHoldCapability,
        projectsProjectOrderReferenceCapability,
        projectsProjectAccessDecisionCapability,
        projectsTimeReferenceCapability,
        projectsTimeLocationReferenceCapability,
        projectsFinanceProfitabilityIndexCapability,
        projectsReportingProjectIndexCapability,
        projectsPortalProjectSummaryCapability,
        projectsCustomerPortalIntakeAcceptanceCapability,
        projectsAssistantContextCapability,
        projectsAssistantActionProviderCapability,
        projectsAssistantNoteActionProviderCapability,
        projectsAssistantTeamActionProviderCapability,
        'projects.lead-intake.v1',
        'projects.project-team-notification-audience.v2',
        'projects.project-team-notification-audience.v3',
        projectsImportReconciliationCapability,
        projectsFullProjectImportCapability,
        projectsImportResolutionCapability,
        projectsProjectStaffImportCapability,
        projectsProjectFilesImportCapability,
    ],
    permissions: [
        'projects.projects.read',
        projectsProjectManageAllPermission,
        'projects.projects.create',
        'projects.projects.update',
        'projects.projects.archive',
        'projects.closeout.read',
        'projects.closeout.execute',
        'projects.closeout.override',
        'projects.notes.read',
        'projects.notes.create',
        'projects.notes.update',
        'projects.notes.delete',
        'projects.team.read',
        'projects.team.assign',
        'projects.files.read',
        'projects.files.upload',
        'projects.files.delete',
        'projects.files.restore',
        'projects.import.read',
        'projects.import.execute',
        'projects.import.rollback',
        projectsPortalProjectSummaryGrant,
    ],
    settings: [
        'projects.board.configuration',
        'projects.operations.configuration',
        'projects.board.appearance',
        'projects.closeout.configuration',
        'projects.files.configuration',
        'projects.display.portal',
        'projects.copy.portal',
        'projects.detail-launcher.configuration',
        'projects.detail-launcher.preference',
    ],
    copyCatalogs: ['projects.copy.default', 'projects.copy.portal'],
    events: {
        publishes: [
            'projects.project-created.v1',
            'projects.project-archived.v1',
            'projects.project-updated.v1',
            'projects.project-updated.v2',
            'projects.project-time-location-updated.v1',
            'projects.project-closed.v1',
            'projects.project-note-created.v1',
            'projects.project-note-updated.v1',
            'projects.project-note-deleted.v1',
            'projects.project-note-archived.v1',
            'projects.project-team-assigned.v1',
            'projects.project-team-removed.v1',
            'projects.project-file-attached.v1',
            'projects.project-file-deleted.v1',
            'projects.project-file-restored.v1',
            'projects.customer-portal-intake-accepted.v1',
        ],
        subscribes: [],
    },
    server: {
        routes: [{ id: 'projects.routes.projects', priority: 0 }],
        jobs: [],
        subscribers: [],
        slots: [{
                id: projectsInsuranceClaimsReadinessProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'projects.files.read',
                priority: 0,
            }, {
                id: projectsFinanceProfitabilityIndexProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'projects.projects.read',
                priority: 10,
            }, {
                id: projectsReportingProjectIndexProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'projects.projects.read',
                priority: 20,
            }, {
                id: projectsPortalProjectSummaryProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'projects.projects.read',
                priority: 30,
            }, {
                id: projectsCustomerPortalIntakeAcceptanceProviderId,
                requiredCapability: platformOptionalActionProviderHostCapability,
                requiredPermission: 'projects.projects.update',
                priority: 40,
            }, {
                id: projectsAssistantContextProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'projects.projects.read',
                priority: 50,
            }, {
                id: projectsAssistantActionProviderCapability,
                requiredCapability: platformReceiptBoundActionProviderHostCapability,
                requiredPermission: 'projects.projects.read',
                priority: 60,
            }, {
                id: projectsAssistantNoteActionProviderCapability,
                requiredCapability: platformReceiptBoundActionProviderHostCapability,
                requiredPermission: 'projects.projects.read',
                priority: 61,
            }, {
                id: projectsAssistantTeamActionProviderCapability,
                requiredCapability: platformReceiptBoundActionProviderHostCapability,
                requiredPermission: 'projects.projects.read',
                priority: 62,
            }, {
                id: projectsLegacyResolutionProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'projects.projects.read',
                priority: 70,
            }],
    },
    web: {
        routes: [
            { id: 'projects.web.projects', priority: 0 },
            { id: 'projects.web.board', priority: 10 },
            { id: 'projects.web.project-detail', priority: 20 },
        ],
        navigation: [
            { id: 'projects.navigation.projects', priority: 0 },
            { id: 'projects.navigation.board', priority: 10 },
        ],
        slots: [
            { id: 'projects.web.settings.board-configuration', priority: 0 },
            { id: 'projects.web.settings.board-appearance', priority: 10 },
            {
                id: 'projects.web.platform.my-day',
                priority: 17,
                requiredCapability: platformMyDayHostCapability,
                requiredPermission: 'projects.projects.read',
            },
            { id: 'projects.web.settings.detail-launcher-configuration', priority: 20 },
        ],
    },
    mobile: {
        routes: [{ id: 'projects.mobile.projects', priority: 0 }],
        slots: [],
    },
    portal: {
        routes: [],
        slots: [{
                id: 'projects.portal.project-summary',
                requiredCapability: platformPortalHostCapability,
                requiredPermission: projectsPortalProjectSummaryGrant,
                priority: 0,
            }],
    },
    database: {
        schema: 'projects',
        owns: [
            'projects.projects',
            'projects.project_members',
            'projects.lock_current_membership_project_access_v1',
            'projects.project_notes',
            'projects.project_timeline_entries',
            'projects.project_closeouts',
            'projects.project_files',
            'projects.project_folders',
            'projects.project_files_import_runs',
            'projects.project_files_import_identity_map',
            'projects.project_files_import_receipts',
            'projects.project_files_import_changes',
            'projects.project_files_import_rollbacks',
            'projects.before_project_folder_update',
            'projects.project_files_import_deterministic_id',
            'projects.project_folder_import_path_segment',
            'projects.project_folder_import_canonical',
            'projects.project_file_import_canonical',
            'projects.project_files_import_receipt_json',
            'projects.project_files_import_assert_project_resolution',
            'projects.project_files_import_assert_membership_resolution',
            'projects.import_runs',
            'projects.import_identity_map',
            'projects.import_row_receipts',
            'projects.import_rejects',
            'projects.import_rollback_receipts',
            'projects.import_target_runs',
            'projects.import_target_identity_map',
            'projects.import_target_row_receipts',
            'projects.import_target_changes',
            'projects.import_target_change_rollbacks',
            'projects.import_target_evidence_immutable',
            'projects.import_canonical_json',
            'projects.import_sha256',
            'projects.import_timestamp_text',
            'projects.import_deterministic_project_id',
            'projects.import_payload_has_exact_keys',
            'projects.import_project_target_canonical',
            'projects.import_expected_project_target',
            'projects.import_assert_relationship_resolution',
            'projects.import_validate_project_target',
            'projects.import_validate_project_payload',
            'projects.import_project_receipt_json',
            'projects.begin_import_target_run_v1',
            'projects.restore_import_target_v1',
            'projects.apply_import_target_v1',
            'projects.rollback_import_target_v1',
            'projects.verify_import_resolution_v1',
            'projects.begin_project_staff_import_v1',
            'projects.apply_project_staff_import_v1',
            'projects.rollback_project_staff_import_v1',
            'projects.begin_project_files_import_v1',
            'projects.apply_project_files_import_v1',
            'projects.rollback_project_files_import_v1',
            'projects.apply_project_file_import_v1',
            'projects.rollback_project_file_import_v1',
            'projects.current_billing_recipient_snapshot',
            'projects.current_project_reference_snapshot',
            'projects.lock_current_project_reference_and_access_v1',
            'projects.resolve_project_team_notification_audience_v1',
            'projects.current_project_order_reference_snapshot',
            'projects.current_project_access_decision',
            'projects.current_project_access_decision_v1',
            'projects.current_project_access_decision_v2',
            'projects.current_membership_can_read_project_history_v1',
            'projects.current_membership_can_read_project',
            'projects.current_time_reference_snapshot',
            'projects.list_projects_v1',
            'projects.project_time_locations',
            'projects.project_time_location_commands',
            'projects.current_time_location_reference_snapshot',
            'projects.match_time_location_references',
            'projects.set_project_time_location',
            'projects.project_intake_acceptance_facts',
            'projects.accept_customer_portal_intake_v1',
            'projects.accept_customer_portal_intake_v2',
            'projects.assistant_project_update_origins',
            'projects.assistant_project_update_restoration_commands',
            'projects.assistant_project_update_restoration_evidence',
            'projects.reject_assistant_project_update_evidence_mutation_v1',
            'projects.guard_assistant_project_restoration_command_mutation_v1',
            'projects.ecmascript_trim_v1',
            'projects.utf16_length_v1',
            'projects.canonical_json_v1',
            'projects.sha256_json_v1',
            'projects.assistant_project_snapshot_v1',
            'projects.assistant_project_changed_fields_v1',
            'projects.assistant_project_changed_fields_match_v1',
            'projects.assistant_project_restoration_patch_v1',
            'projects.lock_assistant_approved_action_receipt_v1',
            'projects.lock_current_assistant_project_v1',
            'projects.replay_assistant_project_update_v1',
            'projects.apply_assistant_project_update_v1',
            'projects.prepare_assistant_project_update_restoration_v1',
            'projects.replay_assistant_project_update_restoration_v1',
            'projects.restore_assistant_project_update_v1',
            'projects.assistant_project_create_origins',
            'projects.assistant_project_archive_commands',
            'projects.reject_assistant_project_create_archive_evidence_mutation_v1',
            'projects.assistant_project_optional_text_valid_v1',
            'projects.normalize_assistant_project_create_input_v1',
            'projects.assert_assistant_project_create_configuration_v1',
            'projects.assistant_project_create_origin_hash_v1',
            'projects.lock_assistant_project_approved_action_receipt_v1',
            'projects.apply_assistant_project_create_v1',
            'projects.archive_assistant_created_project_v1',
            'projects.assistant_project_note_create_origins',
            'projects.assistant_project_note_archive_commands',
            'projects.reject_assistant_project_note_evidence_mutation_v1',
            'projects.reject_assistant_project_note_archive_insert_v1',
            'projects.zod_uuid_valid_v1',
            'projects.zod_uuid_cast_v1',
            'projects.sha256_utf8_text_v1',
            'projects.normalize_assistant_project_note_create_input_v1',
            'projects.normalize_assistant_project_note_archive_input_v1',
            'projects.assistant_project_note_origin_readback_v1',
            'projects.assistant_project_note_record_readback_v1',
            'projects.assistant_project_note_archive_command_readback_v1',
            'projects.guard_assistant_project_note_origin_update_v1',
            'projects.assistant_project_note_delete_fence_v1',
            'projects.assistant_project_note_origin_hash_v1',
            'projects.lock_assistant_project_note_approved_action_receipt_v1',
            'projects.apply_assistant_project_note_create_v1',
            'projects.archive_assistant_created_project_note_v1',
            'projects.assistant_project_team_assignment_create_origins',
            'projects.assistant_project_team_assignment_removal_commands',
            'projects.advance_project_member_revision_v1',
            'projects.apply_assistant_project_team_assignment_v1',
            'projects.assistant_project_team_assignment_origin_hash_v1',
            'projects.assistant_project_team_assignment_origin_readback_v1',
            'projects.assistant_project_team_assignment_record_readback_v1',
            'projects.assistant_project_team_assignment_removal_command_readback_v1',
            'projects.lock_project_team_assignment_approved_action_receipt_v1',
            'projects.normalize_assistant_project_team_assignment_create_input_v1',
            'projects.normalize_assistant_project_team_assignment_removal_input_v1',
            'projects.reject_assistant_project_team_assignment_evidence_mutation_v1',
            'projects.remove_assistant_created_project_team_assignment_v1',
            'projects.resolve_and_hold_project_team_notification_audience_v2',
            'projects.lock_project_team_audience_write_statement_v1',
            'projects.resolve_and_hold_project_team_notification_audience_v3',
        ],
        readsThrough: [
            'platform.storage.v1',
            platformStorageImportHandoffCapability,
            'platform.migration-run-ledger.v1',
            'platform.import-resolution.v1',
            'platform.membership-directory.v1',
            platformApprovedActionInvocationReceiptCapability,
        ],
        migrations: [
            'projects.projects_foundation',
            'projects.lifecycle_notes_timeline',
            'projects.closeout_host',
            'projects.team_assignments',
            'projects.record_write_access',
            'projects.storage_foundation',
            'projects.billing_recipient_snapshot',
            'projects.project_reference',
            'projects.project_order_reference',
            'projects.project_access_decision',
            'projects.assignment_access_v2',
            'projects.time_reference',
            'projects.time_location_reference',
            'projects.customer-portal-intake-acceptance',
            'projects.customer-portal-intake-asset-transfer',
            'projects.team-notification-audience',
            'projects.record-access-enforcement',
            'projects.project-reference-record-access-and-hold',
            'projects.assistant-update-reversal',
            'projects.assistant-create-archive',
            'projects.archived-current-reference-fences',
            'projects.assistant-note-create-archive',
            'projects.assistant-team-assignment',
            'projects.project-team-notification-audience-hold',
            'projects.portal-project-team-notification-audience-v3',
            'projects.import-reconciliation',
            'projects.import-target-writes',
            'projects.import-resolution',
            'projects.project-staff-import',
            'projects.project-files-import',
            'projects.project-file-import-apply',
            'projects.project-file-coordinate-precision',
            'projects.project-file-incomplete-gps-evidence',
            'projects.list-projects-v1',
        ],
    },
    health: ['projects.database'],
    lifecycle: {
        initialization: { timeoutMs: 10_000, failureBehavior: 'reject-release' },
        resources: {
            requestConcurrency: 128,
            jobConcurrency: 0,
            queueBacklogLimit: 0,
            databaseConnectionBudget: 24,
            workerIsolation: 'shared-api',
        },
        disable: {
            data: 'retain',
            clientContributions: 'remove',
            serverRoutes: 'deny-tenant-access',
            scheduledJobs: 'skip',
            events: {
                behavior: 'drop-safe',
                durableCursor: false,
                retentionDays: 0,
                backlogLimit: 0,
                warningAt: 0,
                reconciliationProcedure: null,
                dropSafeReason: 'Projects is mandatory and the current slice owns no inbound event subscriber.',
            },
            outboundSideEffects: 'suppress-no-retroactive-send',
            inFlight: 'finish-idempotently',
        },
        resume: {
            configuration: 'last-compatible-published',
            healthChecks: ['projects.database'],
            reconciliationProcedure: null,
            visibility: 'after-health-and-reconciliation',
        },
        uninstall: {
            executableCode: 'remove',
            data: 'retain',
            tombstone: 'retain-immutable',
            destruction: 'separate-explicit-authority',
        },
    },
    retentionPolicy: 'Projects are mandatory and retained under the tenant data-retention policy.',
});
//# sourceMappingURL=projects.module.js.map